import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { events as evtApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { CheckCircle2, Loader2, XCircle, Ticket } from 'lucide-react';

// Landing after a Stripe resale checkout completes. Calls /verify to transfer
// the ticket to the buyer. Idempotent — safe to reload.
export default function ResaleSuccess() {
  const { eid, listingId } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState('verifying');
  const [ticket, setTicket] = useState(null);
  const [errMsg, setErrMsg] = useState('');
  const { refresh } = useAuth();

  useEffect(() => {
    if (!sessionId) { setState('error'); setErrMsg('Missing Stripe session — please try again.'); return; }
    (async () => {
      try {
        const r = await evtApi.verifyResale(eid, listingId, sessionId);
        setTicket(r.ticket);
        await refresh();
        setState('ok');
      } catch (err) {
        setErrMsg(err?.response?.data?.detail || err?.message || 'Verification failed.');
        setState('error');
      }
    })();
    // eslint-disable-next-line
  }, [eid, listingId, sessionId]);

  return (
    <div className="max-w-md mx-auto pbb-card p-8 text-center space-y-4" data-testid="resale-success-page">
      {state === 'verifying' && (
        <>
          <Loader2 className="mx-auto animate-spin text-green-400" size={30}/>
          <div className="font-semibold text-white">Confirming your resale purchase…</div>
        </>
      )}
      {state === 'ok' && ticket && (
        <>
          <CheckCircle2 className="mx-auto text-green-400" size={40}/>
          <div className="font-bold text-white text-xl">You got the ticket 🎟️</div>
          <div className="text-sm text-gray-300">Your resale ticket for <b>{ticket.eventTitle}</b> is confirmed. Email and SMS on the way.</div>
          <div className="pt-2">
            <div className="text-[10px] tracking-widest text-gray-500 uppercase">Ticket ID</div>
            <div className="font-mono font-bold text-white tracking-widest">#{ticket.ticketId}</div>
          </div>
          <Link to={`/tickets/${ticket.ticketId}`} data-testid="resale-success-view-ticket"
            className="pbb-btn-green mt-3 inline-flex items-center gap-2">
            <Ticket size={14}/> View my ticket
          </Link>
        </>
      )}
      {state === 'error' && (
        <>
          <XCircle className="mx-auto text-red-500" size={36}/>
          <div className="font-semibold text-white">Something went wrong</div>
          <div className="text-sm text-gray-400">{errMsg}</div>
          <Link to="/resale" className="text-green-400 text-sm mt-2 inline-block">Back to resale market</Link>
        </>
      )}
    </div>
  );
}
